import {Injectable} from '@angular/core';
import {GLOBALS} from '@/_services/globals.service';
import {CardConfig, GameMode, PlayerMode, TactaService} from '@/_services/tacta.service';
import {Utils} from '@/classes/utils';

class PathData {
  dir: string;
  score: string;
  disabled: boolean;
}

type Point = { x: number; y: number };

export class TactaCanvas {
  ctx: CanvasRenderingContext2D;
  info: string;
  paths: { [key: string]: Path2D } = {};
  points: { [key: string]: Point[] } = {};
  cardRect: Path2D;
  isMarked = false;
  xOrg: number;
  yOrg: number;
  turnIdx: number;
  playerIdx: number;
  playerHand: string; // f = front of hand, b = back of hand
  cardWidth: number;
  cardHeight: number;
  cardBorder: number;
  cards: TactaCanvas[] = [];
  parentLink: string;
  cardIdx: number;
  markedAreas: string = '';
  errorAreas: string = '';

  constructor(src: any, public ts: TactaService) {
    this.ctx = src.ctx;
    this.paths = {};
    this.points = {};
    this.cardIdx = src.cardIdx ?? -1;
    this.turnIdx = src.turnIdx ?? -1;
    this.playerIdx = src.playerIdx ?? 0;
    this.playerHand = src.playerHand ?? 'f';
    this.parentLink = src.parentLink;
    this.scale = src.scale ?? 1;
    this.cardWidth = src.cardWidth ?? CardConfig.defWidth * this.scale;
    this.cardHeight = src.cardHeight ?? CardConfig.defHeight * this.scale;
    this.cardBorder = src.cardBorder ?? CardConfig.defBorder * this.scale;
    this.xOrg = src.xMid - this.cardWidth / 2;
    this.yOrg = src.yMid - this.cardHeight / 2;
    // for (const card of (src.linkedCards ?? [])) {
    //   this.cards.push(this.ts.createTactaCanvas({
    //     ...card,
    //     ctx: this.ctx,
    //     cardWidth: this.cardWidth,
    //     cardHeight: this.cardHeight,
    //     cardBorder: this.cardBorder,
    //     xMid: this.xOrg,
    //     yMid: this.yOrg
    //   }));
    // }
    this.markedAreas = src.markedAreas;
  }

  _scale = 1;

  get scale() {
    return this._scale;
  }

  set scale(scale: number) {
    this._scale = scale;
    this.cardWidth = CardConfig.defWidth * scale;
    this.cardHeight = CardConfig.defHeight * scale;
    this.cardBorder = CardConfig.defBorder * scale;
    for (const card of this.cards ?? []) {
      card.scale = scale;
    }
  }

  get config(): CardConfig {
    let idx = this.cardIdx;
    if (idx >= TactaService.suiteIdx) {
      return this.ts.deck[idx];
    }
    if (idx > 128) {
      idx -= 128;
      this.ts.deck[idx].flipped = true;
    } else if (this.ts.deck[idx] != null) {
      this.ts.deck[idx].flipped = false;
    }

    return this.ts.deck[idx];
  }

  static fromString(src: string, cards: TactaCanvas[], ts: TactaService, i = 0): number {
    if (i === 0) {
      let pos = src?.indexOf('@');
      if (pos >= 0) {
        // extract player data
        const color = +src.substring(0, 1);
        const s = src.substring(1, pos);
        let pos1 = s.indexOf('[');
        if (pos1 >= 0) {
          const name = s.substring(0, pos1);
          src = src.substring(pos1 + 2);
          pos1 = src.indexOf(']');
          if (pos1 >= 0) {
            const cs = src.substring(0, pos1);
            src = src.substring(pos1 + 1);
            const pc: number[] = [];
            for (let i = 0; i < cs.length; i += 2) {
              pc.push(Number.parseInt(cs.substring(i, i + 2), 16));
            }
            ts.createPlayer(name, color, pc);
            return TactaCanvas.fromString(src, cards, ts);
          }
        }
        src = src.substring(pos + 1);
      }
    }
    // extract board data
    while (i < src.length && src[i] !== ']') {
      const cardIdx = parseInt(src.substring(i, i + 2), 16);
      const turnIdx = parseInt(src.substring(i + 2, i + 4), 16);
      const playerIdx = parseInt(src.substring(i + 4, i + 5), 16);
      const playerHand = src.substring(i + 5, i + 6);
      const parentLink = src.substring(i + 6, i + 8);
      const card = new TactaCanvas({cardIdx, turnIdx, playerIdx, playerHand, parentLink}, ts);
      // if card is not on board and not assigned to player create a new player
      if (card.playerIdx >= 0 && card.playerIdx <= 5 && card.playerIdx >= ts.players.length) {
        ts.createPlayer($localize`Spieler ${ts.players.length + 1}`, ts.cardColor(card.cardIdx));
      }
      cards.push(card);
      ts.removeCardFromPlayers(card.cardIdx);
      i += 8;
      if (src[i] === '[') {
        i++;
        if (src[i] !== ']') {
          while (i < src.length && src[i] !== ']') {
            i = TactaCanvas.fromString(src, card.cards, ts, i);
          }
        }
      }
    }
    return i + 1;
  }

  asString(withPlayer = true): string {
    const ret: string[] = [];
    if (withPlayer) {
      for (const player of this.ts.players) {
        ret.push(player.color.toString());
        ret.push(player.name);
        ret.push('[');
        for (const card of player.cards) {
          ret.push(Utils.hex(card));
        }
        ret.push(']');
      }
      ret.push('@');
    }
    ret.push(`${Utils.hex(this.cardIdx)}${Utils.hex(this.turnIdx)}${Utils.hex(this.playerIdx, 1)}${this.playerHand}${this.parentLink ?? '--'}`);
    ret.push('[');
    if (this.cards.length > 0) {
      for (const link of this.cards) {
        ret.push(link.asString(false));
      }
    }
    ret.push(']');
    return Utils.join(ret, '');
  }

  changeColor(dstColor: number, srcColor?: number): void {
    const currColor = this.ts.cardColor(this.cardIdx);
    if (srcColor == null) {
      srcColor = this.ts.cardColor(this.cardIdx);
      this.cardIdx = this.cardIdx - srcColor * 18 + dstColor * 18;
    } else if (currColor === srcColor) {
      this.cardIdx = this.cardIdx - srcColor * 18 + dstColor * 18;
    }
    for (const card of this.cards) {
      card.changeColor(dstColor, srcColor);
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class TactaCardService {
  /**
   * Draw the board with all cards in the order of their turns
   */
  _lastMaxIdx: number;
  htmlCanvas: HTMLCanvasElement;
  xCvs: number;
  yCvs: number;
  domRect: DOMRect;
  ctx!: CanvasRenderingContext2D;
  xMid: number;
  yMid: number;
  xDown: number;
  yDown: number;

  constructor(public ts: TactaService) {
  }

  _useSaved = false;

  get useSaved() {
    return this._useSaved;
  }

  set useSaved(value: boolean) {
    if (!value || this.hasSaved) {
      this._useSaved = value;
    }
  }

  get hasSaved() {
    return !Utils.isEmpty(localStorage.getItem('tacta-game'));
  }

  drawScore(cvs: TactaCanvas, dir: string, score: string, xm: number, ym: number, size: number) {
    if (GLOBALS.isDebug) {
      let text = dir;
      switch (this.ts.scoreType) {
        case 2:
          text = cvs.info ?? `${Utils.hex(cvs.cardIdx)}`;
          break;
      }
      this.centerText(cvs, text, xm, ym, 1, score === '0' ? cvs.config.colors.s : null);
    } else if (score !== '0') {
      cvs.ctx.fillStyle = cvs.markedAreas?.indexOf(dir) >= 0 ? cvs.config.colors.cm : cvs.config.colors.s;
      cvs.ctx.arc(xm, ym, size, 0, Math.PI * 2);
    }
  }

  drawCorner3(cvs: TactaCanvas, data: PathData) {
    const bw = cvs.cardBorder;
    const x = bw / 2;
    const y = bw / 2;
    const cw = cvs.cardWidth - bw;
    const ch = cvs.cardHeight - bw;
    const w = cw / 2 - bw * 2;
    cvs.ctx.lineJoin = 'round';
    cvs.ctx.lineCap = 'round';
    switch (data.dir) {
      case 'a':
        this.drawPath(cvs, [
          [x + bw, y + bw],
          [x + bw + w, y + bw],
          [x + bw, y + bw + w]], data);
        break;
      case 'b':
        this.drawPath(cvs, [
          [x + cw - bw - w, y + bw],
          [x + cw - bw, y + bw],
          [x + cw - bw, y + bw + w]], data);
        break;
      case 'c':
        this.drawPath(cvs, [
          [x + cw - bw, y + ch - bw - w],
          [x + cw - bw, y + ch - bw],
          [x + cw - bw - w, y + ch - bw]], data);
        break;
      case 'd':
        this.drawPath(cvs, [
          [x + bw, y + ch - bw],
          [x + bw + w, y + ch - bw],
          [x + bw, y + ch - bw - w]], data);
        break;
    }
  }

  drawCorner4(cvs: TactaCanvas, data: PathData) {
    const bw = cvs.cardBorder;
    const x = bw / 2;
    const y = bw / 2;
    const cw = cvs.cardWidth;
    const ch = cvs.cardHeight;
    const w = cw / 2 - bw;
    cvs.ctx.lineJoin = 'round';
    cvs.ctx.lineCap = 'round';
    switch (data.dir) {
      case 'e':
        this.drawPath(cvs, [
          [x + bw, y + bw], [x + w, y + bw],
          [x + w, y + w], [x + bw, y + w]
        ], data);
        break;
      case 'f':
        this.drawPath(cvs, [
          [x + cw / 2, y + bw], [x + cw - bw * 2, y + bw],
          [x + cw - bw * 2, y + w], [x + cw / 2, y + w]
        ], data);
        break;
      case 'g':
        this.drawPath(cvs, [
          [x + cw / 2, y + ch - w - bw], [x + cw - bw * 2, y + ch - w - bw],
          [x + cw - bw * 2, y + ch - bw * 2], [x + cw / 2, y + ch - bw * 2]
        ], data);
        break;
      case 'h':
        this.drawPath(cvs, [
          [x + bw, y + ch - w - bw], [x + w, y + ch - w - bw],
          [x + w, y + ch - bw * 2], [x + bw, y + ch - bw * 2]
        ], data);
        break;
    }
  }

  drawEdge3(cvs: TactaCanvas, data: PathData) {
    const bw = cvs.cardBorder;
    const x = cvs.cardWidth / 2;
    const y = bw / 2 + bw;
    const cw = cvs.cardWidth - bw;
    const w = cw / 2 - bw * 2;
    const dx = Math.sqrt(w * w + w * w) / 2;
    cvs.ctx.lineJoin = 'round';
    cvs.ctx.lineCap = 'round';
    switch (data.dir) {
      case 'i':
        this.drawPath(cvs, [
          [x - dx, y],
          [x + dx, y],
          [x, y + dx]], data);
        break;
      case 'j':
        this.drawPath(cvs, [
          [x + w + bw - dx, cvs.cardHeight / 2],
          [x + w + bw, cvs.cardHeight / 2 - dx],
          [x + w + bw, cvs.cardHeight / 2 + dx]], data);
        break;
      case 'k':
        this.drawPath(cvs, [
          [x, y + cvs.cardHeight - 3 * bw - dx],
          [x + dx, y + cvs.cardHeight - 3 * bw],
          [x - dx, y + cvs.cardHeight - 3 * bw]], data);
        break;
      case 'l':
        this.drawPath(cvs, [
          [x - w - bw + dx, cvs.cardHeight / 2],
          [x - w - bw, cvs.cardHeight / 2 + dx],
          [x - w - bw, cvs.cardHeight / 2 - dx]], data);
        break;
    }
  }

  drawEdge4(cvs: TactaCanvas, data: PathData) {
    const bw = cvs.cardBorder;
    const xm = cvs.cardWidth / 2;
    const ym = cvs.cardHeight / 2;
    let x = bw + bw / 2;
    let y = bw + bw / 2;
    const cw = cvs.cardWidth - 3 * bw;
    const ch = (cvs.cardHeight - cvs.cardWidth) / 2;
    cvs.ctx.lineJoin = 'round';
    cvs.ctx.lineCap = 'round';
    switch (data.dir) {
      // @ts-ignore
      case 'o':
        y = y + cvs.cardHeight - 3 * bw - ch;
      // fallthrough
      case 'm':
        this.drawPath(cvs, [
          [xm - cw / 2, y], [xm + cw / 2, y],
          [xm + cw / 2, y + ch], [xm - cw / 2, y + ch]
        ], data);
        break;
      // @ts-ignore
      case 'n':
        x = x + cvs.cardWidth - 3 * bw - ch;
      // fallthrough
      case 'p':
        this.drawPath(cvs, [
          [x, ym - cw / 2], [x + ch, ym - cw / 2],
          [x + ch, ym + cw / 2], [x, ym + cw / 2]
        ], data);
        break;
    }
  }

  drawSide4(cvs: TactaCanvas, data: PathData) {
    const bw = cvs.cardBorder;
    const ym = cvs.cardHeight / 2;
    let x = bw + bw / 2;
    const cw = cvs.cardHeight - 3 * bw;
    const ch = (cvs.cardHeight - cvs.cardWidth) / 2;
    cvs.ctx.lineJoin = 'round';
    cvs.ctx.lineCap = 'round';
    switch (data.dir) {
      // @ts-ignore
      case 'q':
        x = x + cvs.cardWidth - 3 * bw - ch;
      // fallthrough
      case 'r':
        this.drawPath(cvs, [
          [x, ym - cw / 2], [x + ch, ym - cw / 2],
          [x + ch, ym + cw / 2], [x, ym + cw / 2]
        ], data);
        break;
    }
  }

  drawPath(cvs: TactaCanvas, points: number[][], data: PathData, lw = cvs.cardBorder): void {
    if (data.disabled) {
      return;
    }
    cvs.ctx.beginPath();
    cvs.ctx.moveTo(points[0][0], points[0][1]);
    const srcPath = new Path2D();
    srcPath.moveTo(points[0][0], points[0][1]);
    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      srcPath.lineTo(p[0], p[1]);
      cvs.ctx.lineTo(p[0], p[1]);
    }
    srcPath.closePath();
    const path = new Path2D();
    path.addPath(srcPath, cvs.ctx.getTransform());
    cvs.paths[data.dir] = path;
    cvs.points[data.dir] = points.map((p) => cvs.ctx.getTransform().transformPoint({x: p[0], y: p[1]}));
    const marked = data.dir !== '' && cvs.markedAreas?.indexOf(data.dir) >= 0;
    const hasError = data.dir !== '' && cvs.errorAreas?.indexOf(data.dir) >= 0;
    cvs.ctx.closePath();
    cvs.ctx.lineWidth = lw;
    const config = cvs.config;
    cvs.ctx.strokeStyle = marked ? config.colors.sm : config.colors.s;
    if (hasError) {
      cvs.ctx.fillStyle = config.colors.er;
      cvs.ctx.fill();
    } else if (marked || (data.score !== '0' && data.score !== '-')) {
      cvs.ctx.fillStyle = marked ? config.colors.fm : config.colors.f;
      cvs.ctx.fill();
    }
    cvs.ctx.stroke();

    // this.ctx.strokeStyle = 'yellow';
    // this.ctx.lineWidth = 1;
    // const w = (this.config.width + 2 * this.config.border) / 4;
    // this.ctx.moveTo(w, 0);
    // this.ctx.lineTo(w, this.config.height);
    // this.ctx.moveTo(0, w);
    // this.ctx.lineTo(this.config.width, w);
    // this.ctx.moveTo(this.config.width - w, 0);
    // this.ctx.lineTo(this.config.width - w, this.config.height);
    // this.ctx.stroke();

    if (data.score !== '') {
      cvs.ctx.beginPath();
      let xm = 0;
      let ym = 0;
      for (const p of points) {
        xm += p[0];
        ym += p[1];
      }
      xm = xm / points.length;
      ym = ym / points.length;
      const bw = cvs.cardBorder;
      let dx: number;
      let dy: number;
      const size = cvs.cardBorder * 2;
      cvs.ctx.fillStyle = 'white';
      switch (data.score) {
        case '0':
        case '1':
          this.drawScore(cvs, data.dir, data.score, xm, ym, size);
          break;
        case '2':
          dx = cvs.cardWidth / 12 + bw / 2;
          dy = dx;
          if (xm > cvs.cardWidth / 2) {
            dy = -dy;
          }
          if (ym > cvs.cardHeight / 2) {
            dx = -dx;
          }
          this.drawScore(cvs, data.dir, data.score, xm + dx, ym + dy, size);
          this.drawScore(cvs, data.dir, data.score, xm - dx, ym - dy, size);
          break;
        case '3':
          this.drawScore(cvs, data.dir, data.score, xm, ym, size);
          dx = cvs.cardWidth / 4 + bw / 2;
          dy = 0;
          if (data.dir === 'n' || data.dir === 'p') {
            dy = dx;
            dx = 0;
          }
          this.drawScore(cvs, data.dir, data.score, xm + dx, ym + dy, size);
          this.drawScore(cvs, data.dir, data.score, xm - dx, ym - dy, size);
          break;
        case '4':
          dy = (cvs.cardHeight - bw * 3) / 5;
          ym -= dy * 1.5;
          for (let i = 0; i < 4; i++) {
            this.drawScore(cvs, data.dir, data.score, xm, ym + dy * i, size);
          }
          break;
      }
      cvs.ctx.fill();
    }
  }

  /**
   * Renders a playing card on the specified canvas,
   * considering alignment, rotation, and configuration options.
   *
   * @param {TactaCanvas} cvs - The canvas and associated properties used for rendering the card.
   * @param {number} [turnIdx=-1] - The index of the turn to determine whether the card should be rendered or skipped.
   */
  drawCard(cvs: TactaCanvas, turnIdx: number = -1): void {
    if (cvs?.ctx == null) {
      return;
    }
    cvs.ctx.save();
    const link = this.ts.connectors[cvs.parentLink];
    const config = cvs.config;
    if (link != null) {
      cvs.ctx.translate(50 * cvs.cardWidth / 100, 50 * cvs.cardHeight / 100);
      cvs.ctx.translate(link.x * cvs.cardWidth / 100, link.y * cvs.cardHeight / 100);
      cvs.ctx.rotate(link.deg * Math.PI / 180);
    } else {
      cvs.ctx.translate(cvs.xOrg, cvs.yOrg);
    }

    if (turnIdx < 0 || turnIdx === cvs.turnIdx) {
      cvs.ctx.strokeStyle = cvs.isMarked ? config.colors.sm : 'white';
      cvs.ctx.fillStyle = cvs.isMarked ? config.colors.dm : 'black';

      cvs.paths = {};
      const bw = cvs.cardBorder;
      let x = bw / 2;
      let y = bw / 2;
      let w = cvs.cardWidth - bw;
      let h = cvs.cardHeight - bw;
      cvs.ctx.fillRect(x + bw, y + bw, w - bw * 2, h - bw * 2);

      const path = new Path2D();
      path.moveTo(x + bw, y + bw);
      path.lineTo(x + w - bw, y + bw);
      path.lineTo(x + w - bw, y + h - bw);
      path.lineTo(x + bw, y + h - bw);
      path.closePath();
      cvs.points = {
        '@': [
          [x + bw, y + bw], [x + w - bw, y + bw],
          [x + w - bw, y + h - bw], [x + bw, y + h - bw]
        ].map((p) => cvs.ctx.getTransform().transformPoint({x: p[0], y: p[1]}))
      };
      cvs.cardRect = new Path2D();
      cvs.cardRect.addPath(path, cvs.ctx.getTransform());

      // the outer frame
      cvs.ctx.beginPath();
      cvs.ctx.roundRect(x, y, w, h, bw * 2);
      const hasError = cvs.errorAreas.indexOf('@') >= 0;
      cvs.ctx.strokeStyle = hasError ? config.colors.er : config.colors.f;
      cvs.ctx.lineWidth = bw * (hasError ? 2 : 1);
      cvs.ctx.stroke();
      // the frame for the last card played
      if (cvs.cardIdx > 0 && cvs.turnIdx === this.ts.highestTurn()?.turnIdx) {
        cvs.ctx.beginPath();
        cvs.ctx.roundRect(x, y, w, h, bw * 2);
        cvs.ctx.strokeStyle = config.colors.f + '80';
        cvs.ctx.lineWidth = bw * 5;
        cvs.ctx.stroke();
      }

      // the inner frame
      cvs.ctx.beginPath();
      x += bw;
      y += bw;
      w -= bw * 2;
      h -= bw * 2;
      cvs.ctx.roundRect(x, y, w, h, bw);
      cvs.ctx.strokeStyle = 'white';
      cvs.ctx.lineWidth = bw;
      cvs.ctx.stroke();

      let score = 0;
      // the areas
      for (let i = 0; i < config.areas.length; i += 2) {
        const area = config.areas.substring(i, i + 2);
        const tmp = parseInt(area.substring(1));
        score += isNaN(tmp) ? 0 : tmp;
        if (!cvs.isMarked) {
          this.drawArea(cvs, area);
        }
      }

      const xm = cvs.cardWidth / 2;
      const ym = cvs.cardHeight / 2;
      const size = !cvs.isMarked ? cvs.cardWidth / 9 : cvs.cardWidth / 4;
      cvs.ctx.fillStyle = config.colors.f;
      cvs.ctx.strokeStyle = 'white';
      const data: PathData = {
        dir: '',
        score: '',
        disabled: false
      };
      switch (config.suite) {
        case 0:
          cvs.ctx.beginPath();
          cvs.ctx.arc(xm, ym, size, 0, Math.PI * 2);
          cvs.ctx.fill();
          cvs.ctx.arc(xm, ym, size - cvs.cardBorder / 3, 0, Math.PI * 2);
          cvs.ctx.lineWidth = cvs.cardBorder / 3;
          cvs.ctx.stroke();
          break;
        case 3:
          this.drawPath(cvs, [
            [xm - size, ym + size * 0.6],
            [xm + size, ym + size * 0.6],
            [xm, ym - size],
          ], data, cvs.cardBorder / 2);
          break;
        case 4:
          this.drawPath(cvs, [
            [xm - size, ym - size],
            [xm + size, ym - size],
            [xm + size, ym + size],
            [xm - size, ym + size],
          ], data, cvs.cardBorder / 2);
          break;
      }

      const isSuite = cvs.cardIdx >= TactaService.suiteIdx;
      let text = isSuite ? '' : (score > 0 ? `${score}` : 'X');
      let scale = 1;
      if (isSuite && this.ts.gameMode() === GameMode.Quick) {
        const suiteIdx = (cvs.cardIdx - TactaService.suiteIdx) % 3;
        const player = this.ts.players[cvs.playerIdx];
        const quick = player.quick.find(q => q.suite === suiteIdx);
        if (quick != null && quick.score >= 0) {
          text = `${quick.score}`;
          scale = suiteIdx === 1 ? 0.7 : 1;
        }
      }
      if (GLOBALS.isDebug) {
        text = Utils.hex(cvs.cardIdx);
        scale = 0.7;
      }
      this.centerText(cvs, text, xm, ym, scale);
      if (isSuite && cvs.playerIdx >= 0 && cvs.playerIdx <= 5) {
        const player = this.ts.players[cvs.playerIdx];
        const ch = (cvs.cardHeight - cvs.cardWidth) / 2;
        const suiteIdx = (cvs.cardIdx - TactaService.suiteIdx) % 3;
        switch (this.ts.gameMode()) {
          case GameMode.Sabotage:
            if (player.sabotage[suiteIdx] === 'o') {
              const opp = this.ts.players[(cvs.playerIdx + 1) % this.ts.players.length];
              this.centerText(cvs, opp.name, xm, ch / 2 + 2 * bw, scale);
            }
            if (player.sabotage[suiteIdx] === 's') {
              this.centerText(cvs, player.name, xm, cvs.cardHeight - ch / 2 - 2 * bw, scale);
            }
            break;
          case GameMode.Quick:
            if (player.quick[suiteIdx].active) {
              this.centerText(cvs, player.name, xm, cvs.cardHeight - ch / 2 - 2 * bw, scale);
            }
            break;
        }
      }
      if (turnIdx >= 0) {
        cvs.ctx.restore();
        return;
      }
    }
    for (const card of cvs.cards) {
      this.drawCard(card, turnIdx);
    }
    cvs.ctx.restore();
  }

  drawBoard(cvs: TactaCanvas) {
    if (cvs.ctx == null) {
      return;
    }
    const maxCard = this.ts.highestTurn();
    const maxIdx = maxCard?.turnIdx;
    for (let turnIdx = 0; turnIdx <= maxIdx; turnIdx++) {
      this.drawCard(cvs, turnIdx);
    }
    if (this._lastMaxIdx !== maxCard?.cardIdx) {
      this.checkOverlappingAreas(cvs, maxCard);
      this._lastMaxIdx = maxCard.cardIdx;
      this.drawCard(cvs, maxCard.turnIdx);
    }
    // create frame with maximum coordinates around the board
    let max = 10 + 26 + 26;
    max *= max;
    const w = max * cvs.cardWidth / 100;
    const h = max * cvs.cardHeight / 100;
    cvs.ctx.beginPath();
    cvs.ctx.strokeStyle = 'red';
    cvs.ctx.lineWidth = cvs.cardBorder;
    cvs.ctx.rect(cvs.xOrg - w / 2, cvs.yOrg - h / 2, w, h);
    cvs.ctx.stroke();

    // const card = this.ts.highestTurn(cvs.cards);
    // if (card != null) {
    //   for (const p of Object.values(card.points)) {
    //     cvs.ctx.beginPath();
    //     cvs.ctx.lineWidth = 2;
    //     cvs.ctx.strokeStyle = 'red';
    //     cvs.ctx.moveTo(p[0].x, p[0].y);
    //     for (const q of p) {
    //       cvs.ctx.lineTo(q.x, q.y);
    //     }
    //     cvs.ctx.closePath();
    //     cvs.ctx.stroke();
    //   }
    // }
  }

  centerText(cvs: TactaCanvas, text: string, x: number, y: number, scale = 1, color: string = null) {
    cvs.ctx.textAlign = 'center';
    cvs.ctx.textBaseline = 'middle';
    cvs.ctx.font = `bold ${cvs.scale * 7 * scale}px Verdana`;
    cvs.ctx.fillStyle = color ?? cvs.config.colors.t;
    const metrics = cvs.ctx.measureText(text);
    const textHeight =
      metrics.actualBoundingBoxAscent +
      metrics.actualBoundingBoxDescent;
    y += (metrics.actualBoundingBoxAscent - textHeight / 2);
    cvs.ctx.fillText(text, x, y);
  }

  isDirAvailable(cvs: TactaCanvas, dir: string): boolean {
    // list containing the blocked directions for any given direction
    const blocked: any = {
      a: 'befilmpr',
      b: 'aefijmnq',
      c: 'dghjknoq',
      d: 'cghklopr',
      e: 'abilmpr',
      f: 'abijmnq',
      g: 'cdjknoq',
      h: 'cdklopr',
      i: 'abefmqr',
      j: 'bcfgnq',
      k: 'cdghjloqr',
      l: 'adehpr',
      m: 'abefinpqr',
      n: 'bcfgjq',
      o: 'cdghknpqr',
      p: 'adehlr',
      q: 'bcfgijklmno',
      r: 'adehiklmop'
    }
    for (const card of cvs.cards) {
      if (blocked[card.parentLink?.substring(1, 2)].indexOf(dir) >= 0) {
        return false;
      }
    }
    return true;
  }

  drawArea(cvs: TactaCanvas, area: string): void {
    const data: PathData = new PathData();
    data.dir = area.substring(0, 1);
    data.disabled = !this.isDirAvailable(cvs, data.dir);
    data.score = area.substring(1);
    if ('abcd'.indexOf(data.dir) >= 0) {
      this.drawCorner3(cvs, data);
    }
    if ('efgh'.indexOf(data.dir) >= 0) {
      this.drawCorner4(cvs, data);
    }
    if ('ijkl'.indexOf(data.dir) >= 0) {
      this.drawEdge3(cvs, data);
    }
    if ('mnop'.indexOf(data.dir) >= 0) {
      this.drawEdge4(cvs, data);
    }
    if ('qr'.indexOf(data.dir) >= 0) {
      this.drawSide4(cvs, data);
    }
  }

  updateContext(cvs: TactaCanvas, ctx: CanvasRenderingContext2D) {
    cvs.ctx = ctx;
    cvs.cardWidth = CardConfig.defWidth * cvs.scale;
    cvs.cardHeight = CardConfig.defHeight * cvs.scale;
    cvs.cardBorder = CardConfig.defBorder * cvs.scale;
    for (const card of cvs.cards) {
      this.updateContext(card, ctx);
    }
  }

  moveMarkedToBoard(board: TactaCanvas, dst: TactaCanvas) {
    const src = this.ts.markedCanvas();
    if (src == null || dst == null) {
      return;
    }
    src.parentLink = `${src.markedAreas}${dst.markedAreas}`;
    src.ctx = board.ctx;
    src.scale = board.scale;
    src.cardWidth = board.cardWidth;
    src.cardHeight = board.cardHeight;
    src.cardBorder = board.cardBorder;
    src.turnIdx = (this.ts.highestTurn()?.turnIdx ?? 0) + 1;
    src.markedAreas = '';
    src.errorAreas = '';
    src.isMarked = false;
    const player = this.ts.players.find(p => p.playerIdx === src.playerIdx);
    src.playerHand = player?.cards?.findIndex(c => c === src.cardIdx) === 0 ? 'f' : 'b';
    dst.cards.push(src);
    this.ts.playerMode.set(PlayerMode.PlaceCard);
    this.ts.clearMarkedAreas();
    this.ts.calcScore(board);
  }

  checkAreas(src: TactaCanvas, dst: TactaCanvas) {
    if (dst.cardIdx === src.cardIdx) {
//      console.log('nix check', Utils.hex(src.cardIdx), Utils.hex(dst.cardIdx), dst);
      return;
    }
    if (!(dst.cards ?? []).some(c => c.cardIdx === src.cardIdx)) {
//      console.log('check', Utils.hex(src.cardIdx), Utils.hex(dst.cardIdx), src);
      for (const srcKey of Object.keys(src.points)) {
        for (const dstKey of Object.keys(dst.points)) {
          if (dstKey !== '@' || srcKey !== '@') {
            if (this.hasOverlap(src.points[srcKey], dst.points[dstKey])) {
              const intersectionPoints = this.clip(src.points[srcKey], dst.points[dstKey]);
              if (this.calculateArea(intersectionPoints) > 0) {
                src.errorAreas += srcKey;
//                console.log('OHA', srcKey, Utils.hex(src.cardIdx), Utils.hex(dst.cardIdx), src.errorAreas, src);
              }
            }
          }
        }
        // const srcPath = src.paths[srcKey];
        // let isInside = src.points[srcKey].some(p => dst.ctx.isPointInPath(dst.cardRect, p.x, p.y))
        // if (isInside) {
        //   src.errorAreas += srcKey;
        //   console.log('HUIIII', Utils.hex(src.cardIdx), Utils.hex(dst.cardIdx), src.errorAreas, src);
        // }
        // for (const dstKey of Object.keys(dst.paths)) {
        //   const dstPath = dst.paths[dstKey];
        //   isInside = src.points[srcKey].some(p => dst.ctx.isPointInPath(dstPath, p.x, p.y));
        //   if (isInside) {
        //     src.errorAreas += srcKey;
        //     console.log('OHA', Utils.hex(src.cardIdx), Utils.hex(dst.cardIdx), src.errorAreas, src);
        //   }
        //   isInside = dst.points[dstKey].some(p => src.ctx.isPointInPath(srcPath, p.x, p.y));
        //   if (isInside) {
        //     src.errorAreas += srcKey;
        //     console.log('OHA', Utils.hex(src.cardIdx), Utils.hex(dst.cardIdx), src.errorAreas, src);
        //   }
        // }
      }
    }
    for (const subCard of dst.cards ?? []) {
      if (subCard.cardIdx !== src.cardIdx) {
        this.checkAreas(src, subCard);
      }
    }
  }

  checkOverlappingAreas(board: TactaCanvas, card: TactaCanvas) {
    card.errorAreas = '';
    this.checkAreas(card, board);
    this.ts.mayConfirm.set(card.errorAreas.length === 0);
  }

  boundingBox(points: Point[]): any {
    let minX = points[0].x;
    let maxX = points[0].x;
    let minY = points[0].y;
    let maxY = points[0].y;

    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      if (p.x < minX) {
        minX = p.x;
      } else if (p.x > maxX) {
        maxX = p.x;
      }
      if (p.y < minY) {
        minY = p.y;
      } else if (p.y > maxY) {
        maxY = p.y;
      }
    }
    return {minX, maxX, minY, maxY};
  }

  hasOverlap(p1: Point[], p2: Point[]) {
    const b1 = this.boundingBox(p1);
    const b2 = this.boundingBox(p2);
    return !(b1.maxX < b2.minX || b1.minX > b2.maxX || b1.maxY < b2.minY || b1.minY > b2.maxY);
  }

  getIntersectionArea(poly1: Point[], poly2: Point[]): number {
    // 1. Schnittmenge der Punkte finden
    const intersectionPoints = this.clip(poly1, poly2);

    if (intersectionPoints.length < 3) {
      return 0;
    } // Keine Fläche möglich

    // 2. Fläche der Schnittmenge mit der Shoelace-Formel berechnen
    return this.calculateArea(intersectionPoints);
  }

  clip(subjectPolygon: Point[], clipPolygon: Point[]): Point[] {
    let outputList = subjectPolygon;
    let cp1 = clipPolygon[clipPolygon.length - 1];

    for (const cp2 of clipPolygon) {
      const inputList = outputList;
      outputList = [];
      if (inputList.length === 0) {
        break;
      }

      let s = inputList[inputList.length - 1];

      for (const e of inputList) {
        if (this.isInside(cp1, cp2, e)) {
          if (!this.isInside(cp1, cp2, s)) {
            outputList.push(this.intersect(cp1, cp2, s, e));
          }
          outputList.push(e);
        } else if (this.isInside(cp1, cp2, s)) {
          outputList.push(this.intersect(cp1, cp2, s, e));
        }
        s = e;
      }
      cp1 = cp2;
    }
    return outputList;
  }

  isInside(cp1: Point, cp2: Point, p: Point): boolean {
    // Nutzt das Kreuzprodukt, um zu prüfen, ob der Punkt links der Kante liegt
    return (cp2.x - cp1.x) * (p.y - cp1.y) > (cp2.y - cp1.y) * (p.x - cp1.x);
  }

  intersect(cp1: Point, cp2: Point, s: Point, e: Point): Point {
    const dc = {x: cp1.x - cp2.x, y: cp1.y - cp2.y};
    const dp = {x: s.x - e.x, y: s.y - e.y};
    const n1 = cp1.x * cp2.y - cp1.y * cp2.x;
    const n2 = s.x * e.y - s.y * e.x;
    const n3 = 1.0 / (dc.x * dp.y - dc.y * dp.x);
    return {x: (n1 * dp.x - n2 * dc.x) * n3, y: (n1 * dp.y - n2 * dc.y) * n3};
  }

  calculateArea(points: Point[]): number {
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      area += points[i].x * points[j].y - points[j].x * points[i].y;
    }
    return Math.abs(area) / 2;
  }

  initCanvas(rect?: DOMRect) {
    if (rect != null) {
      this.domRect = rect;
    }
    this.htmlCanvas.width = this.domRect.width;
    this.htmlCanvas.height = this.domRect.height;
    const cvs = this.ts.boardCanvas();
    this.ctx = this.htmlCanvas.getContext('2d')!;
    this.xMid = this.htmlCanvas.width / 2;
    this.yMid = this.htmlCanvas.height / 2;
    // this.cvs = this.ts.createTactaCanvas({...this, scale: 2});
    cvs.scale = this.ts.connectors?.dbg?.scale ?? 2;
    cvs.cardWidth = CardConfig.defWidth * cvs.scale;
    cvs.cardHeight = CardConfig.defHeight * cvs.scale;
    cvs.cardBorder = CardConfig.defBorder * cvs.scale;
    cvs.xOrg = this.htmlCanvas.width / 2 - cvs.cardWidth / 2;
    cvs.yOrg = this.htmlCanvas.height / 2 - cvs.cardHeight / 2;
    this.updateContext(cvs, this.ctx);
    this.ts.clearBoard();
    this.drawBoard(cvs);
    this.ts.calcScore(cvs);
  }

  initGame() {
    this.ts.playerMode.set(PlayerMode.SelectCard);
    switch (this.ts.gameMode()) {
      case GameMode.Normal:
        for (const player of this.ts.players) {
          player.cards = Utils.randomNumbers(18, (player.color - 1) * 18 + 1);
        }
        break;
      case GameMode.Sabotage:
        for (let i = 0; i < this.ts.players.length; i++) {
          const self = this.ts.players[i];
          const opponent = this.ts.players[i === 0 ? this.ts.players.length - 1 : i - 1];
          const oIdx = opponent.sabotage.indexOf('o');
          const sIdx = self.sabotage.indexOf('s');
          const sCards = Utils.randomNumbers(6, (self.color - 1) * 18 + (sIdx * 6) + 1);
          const oCards = Utils.randomNumbers(6, (opponent.color - 1) * 18 + (oIdx * 6) + 1);
          self.cards = [...sCards, ...oCards];
          Utils.randomize(self.cards);
        }
        break;
      case GameMode.Quick:
        for (let i = 0; i < this.ts.players.length; i++) {
          const self = this.ts.players[i];
          const suite = self.quick.find(q => q.active)?.suite;
          if (suite >= 0) {
            const sCards = Utils.randomNumbers(6, (self.color - 1) * 18 + (suite * 6) + 1);
            self.cards = [...sCards];
            Utils.randomize(self.cards);
          }
        }
        break;
    }
    const cvs: TactaCanvas[] = [];
    let src = '000000--[]';
    if (this.useSaved) {
      src = localStorage.getItem('tacta-game');
      this.ts.players = [];
    }
    TactaCanvas.fromString(src, cvs, this.ts);
//    TactaCanvas.fromString('4Andi[424443393c3a3d40373f4648473b4541]2Spieler 2[21241815191a1d13201c1f1b161e222317]@000000--[b80100fe[140201jc[be0300ld[170401ci[]]]]]', cvs, this);
//     TactaCanvas.fromString('4Andi[4443393c3a3d40373f4648]2Spieler 2[191a1d13201c1f1b16]@000000--[b80100fe[140201jc[be0300ld[230401jc[a10601ee[450700ld[]9e0801cj[420900qr[180a01jd[]]]]3b0b00po[950c01jc[c70d00oo[]]]]]]]410500rq[]]', cvs, this);
//     this.playerIdx = 1;
    // this.playerMode.set(PlayerMode.PlaceCard);
    this.ts.board = cvs;
  }

  loadGame(game: string) {
    if (Utils.isEmpty(game)) {
      game = '000000--[]';
    }
    const cvs: TactaCanvas[] = [];
    this.ts.players = [];
    TactaCanvas.fromString(game, cvs, this.ts);
    this.ts.playerIdx = 0;
    this.ts.board = cvs;
    this.initCanvas();
  }

  errorCheck(cvs: TactaCanvas) {
    this.checkOverlappingAreas(this.ts.boardCanvas(), cvs);
    for (const card of cvs.cards) {
      this.errorCheck(card);
    }
    this.ts.refresh.set(!this.ts.refresh());
  }
}
